/**
 * useApi — React hook for API calls with loading/error/data states.
 * Uses the apiFetch from AuthProvider (cookie auth, 401 auto-refresh).
 */

import { useState, useCallback } from 'react';
import { apiFetch } from '../providers/AuthProvider';

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useApi<T = unknown>() {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const get = useCallback(async (url: string): Promise<T | null> => {
    setState({ data: null, loading: true, error: null });
    try {
      const res = await apiFetch(url);
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        setState({ data: null, loading: false, error: text });
        return null;
      }
      const data = (await res.json()) as T;
      setState({ data, loading: false, error: null });
      return data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      setState({ data: null, loading: false, error: msg });
      return null;
    }
  }, []);

  const post = useCallback(async (url: string, body?: unknown): Promise<T | null> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        setState((prev) => ({ ...prev, loading: false, error: text }));
        return null;
      }
      const data = (await res.json()) as T;
      setState((prev) => ({ ...prev, loading: false }));
      return data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      setState((prev) => ({ ...prev, loading: false, error: msg }));
      return null;
    }
  }, []);

  return { ...state, get, post };
}

/**
 * Turn a failed response into an Error whose message the UI can show.
 *
 * The body is normally the API's own JSON (`{"error": "..."}`), which callers
 * unwrap. A gateway failure is different: Cloudflare and friends answer with a
 * full HTML error page, and rendering that verbatim buries the UI in markup. Any
 * non-JSON body collapses to the status line instead.
 */
async function failure(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  const trimmed = text.trim();
  const looksLikeApi =
    trimmed.length > 0 &&
    trimmed.length <= 2000 &&
    (trimmed.startsWith('{') || trimmed.startsWith('['));
  if (looksLikeApi) return new Error(trimmed);
  return new Error(
    JSON.stringify({ error: `${res.status} ${res.statusText || 'Request failed'}`.trim() }),
  );
}

/** Simple fire-and-forget API helpers */
export async function apiGet<T>(url: string): Promise<T> {
  const res = await apiFetch(url);
  if (!res.ok) throw await failure(res);
  return res.json();
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await failure(res);
  return res.json();
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await failure(res);
  return res.json();
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await failure(res);
  return res.json();
}

export async function apiDelete<T>(url: string): Promise<T> {
  const res = await apiFetch(url, { method: 'DELETE' });
  if (!res.ok) throw await failure(res);
  return res.json();
}
